"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { SuggestionListCard } from "@/components/suggestion-lists/suggestion-list-card";
import { Icon } from "@/components/ui/icon";
import { track } from "@/lib/analytics/data-layer";
import type { SuggestionTemplateCard } from "@/lib/create-list/suggestion-templates";
import { suggestionListHref } from "@/lib/suggestion-lists/suggestion-lists";

/** Slack (px) absorbing sub-pixel overflow and rounding at the row's edges. */
const EDGE_TOLERANCE_PX = 8;

/** Gap (px) added to a card's width to get one "advance one card" step. */
const CARD_GAP_PX = 24;

/**
 * `scroll-behavior: smooth` has no completion event, so arrow state is re-read
 * on a ladder of delays that spans a typical smooth scroll.
 */
const RESYNC_DELAYS_MS = [120, 380, 700, 1100];

interface CarouselState {
  /** Whether the row overflows at all — decides if the arrows exist. */
  hasOverflow: boolean;
  canPrev: boolean;
  canNext: boolean;
}

/**
 * Tracks the row's scroll position so the arrows hide when there is nothing to
 * scroll and dim at each end. Measured from the element, never derived from the
 * item count, so it survives the grid/carousel breakpoint and any list count.
 */
function useCarousel(itemCount: number) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<CarouselState>({ hasOverflow: false, canPrev: false, canNext: false });

  const sync = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    const maxScrollLeft = row.scrollWidth - row.clientWidth;
    // Bail out when nothing changed: `sync` runs on every scroll event, and a
    // fresh object would re-render the arrows on each frame.
    setState((prev) => {
      const next = {
        hasOverflow: maxScrollLeft > EDGE_TOLERANCE_PX,
        canPrev: row.scrollLeft > EDGE_TOLERANCE_PX,
        canNext: row.scrollLeft < maxScrollLeft - EDGE_TOLERANCE_PX,
      };
      return prev.hasOverflow === next.hasOverflow && prev.canPrev === next.canPrev && prev.canNext === next.canNext
        ? prev
        : next;
    });
  }, []);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    sync();
    row.addEventListener("scroll", sync, { passive: true });
    // The grid/carousel switch is a media query, so overflow changes on resize.
    const resizeObserver = new ResizeObserver(sync);
    resizeObserver.observe(row);
    return () => {
      row.removeEventListener("scroll", sync);
      resizeObserver.disconnect();
    };
    // `itemCount` matters for when the lists come from the API: in carousel mode
    // extra cards grow scrollWidth without changing the row's own box, so the
    // ResizeObserver alone would leave `hasOverflow` stale.
  }, [sync, itemCount]);

  const scrollByCard = useCallback((direction: 1 | -1) => {
    track({
      event: "carousel_advance",
      direction: direction === 1 ? "next" : "prev",
      list_id: "suggestion_lists",
    });
    const row = rowRef.current;
    if (!row) return;
    // Measure the real card so the step follows the responsive card width;
    // 80% of the viewport is the fallback when the row is empty.
    const firstCard = row.firstElementChild;
    const step = (firstCard ? firstCard.getBoundingClientRect().width : row.clientWidth * 0.8) + CARD_GAP_PX;
    row.scrollBy({ left: direction * step, behavior: "smooth" });
    RESYNC_DELAYS_MS.forEach((delay) => setTimeout(sync, delay));
  }, [sync]);

  return { rowRef, ...state, scrollByCard };
}

// `rounded-[50%]`, not `rounded-full`: Tailwind v4 resolves `rounded-full` to
// `calc(infinity * 1px)`, and the reference specifies 50%.
const ARROW_CLASS =
  "inline-flex size-11 flex-none cursor-pointer items-center justify-center rounded-[50%] " +
  "border border-[var(--tb-border)] bg-transparent text-[var(--fg)] " +
  "transition-[border-color,background-color] duration-[400ms] " +
  "hover:border-[var(--fg)] hover:bg-[var(--tb-porcelain-tint)] " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tb-green focus-visible:ring-offset-2";

/**
 * "Listas de Sugestão" — the house's ready-made lists, so a host can start from a curated
 * selection instead of an empty list.
 *
 * **The lists come from the backend**, read by the server page above (`listSuggestionTemplates`)
 * and handed down. They used to be a constant in the repository, because the template reads
 * needed a customer's Bearer and a visitor here has none; the API opened them in PR #57. The
 * carousel was already written to survive any count, so nothing here had to change for that.
 *
 * An empty list — the backend unreachable, or nothing registered — draws no section at all
 * rather than a heading over a void.
 */
export function SuggestionListsSection({ lists }: { lists: readonly SuggestionTemplateCard[] }) {
  const { rowRef, hasOverflow, canPrev, canNext, scrollByCard } = useCarousel(lists.length);

  if (lists.length === 0) return null;

  return (
    <section id="listas" className="bg-[var(--tb-white)] pb-[clamp(64px,9vh,120px)] pt-[clamp(70px,10vh,140px)]">
      <div className="mx-auto max-w-[1600px] px-[clamp(20px,4vw,56px)]">
        <div className="grid grid-cols-1 items-end gap-[clamp(20px,4vw,64px)] mb-[clamp(34px,4.5vw,64px)] [@media(min-width:900px)]:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
          <div className="flex flex-col gap-[30px]">
            <span className="text-[15px] font-medium uppercase tracking-[0.16em] text-tb-green">Listas de Sugestão</span>
            <h2 data-fade="1" className="font-display text-[clamp(28px,3.52vw,51px)] font-light uppercase leading-[var(--tb-typography-line-height-display)] tracking-[-0.01em] [text-wrap:balance]">
              Vista sua casa com escolhas <span className="text-tb-green">essenciais</span> de Tania.
            </h2>
          </div>
          {/* `items-end` on desktop is what flushes the 460px paragraph to the
              right edge of the column (the reference's `listHeadAlign`). */}
          <div className="flex flex-col items-start gap-[22px] [@media(min-width:900px)]:items-end [@media(min-width:900px)]:gap-[26px] [@media(min-width:900px)]:self-stretch [@media(min-width:900px)]:justify-center [@media(min-width:900px)]:pt-[44px]">
            <p className="max-w-[460px] text-[clamp(15px,1.1vw,17px)] leading-[1.7] text-[var(--fg-muted)] [text-wrap:pretty]">
              Uma curadoria assinada por Tania Bulhões, com criações que unem arte, beleza e excelência para acompanhar momentos e atravessar gerações.
            </p>

            {/*
              Arrows only exist while the row overflows, and stay hidden below
              900px where touch scrolling is the affordance. With three lists the
              desktop grid fits, so they are normally absent — `hasOverflow` is
              measured, so a fourth list brings them back on its own.
            */}
            {hasOverflow ? (
              <div className="hidden items-center gap-2.5 [@media(min-width:900px)]:flex">
                <button
                  type="button"
                  onClick={() => scrollByCard(-1)}
                  aria-label="Lista anterior"
                  className={ARROW_CLASS}
                  style={{ opacity: canPrev ? 1 : 0.35 }}
                >
                  <Icon name="arrow-left" size={17} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollByCard(1)}
                  aria-label="Próxima lista"
                  className={ARROW_CLASS}
                  style={{ opacity: canNext ? 1 : 0.35 }}
                >
                  <Icon name="arrow-right" size={17} />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Single row: snap-scroller below 1000px, 3-up grid above (see globals.css). */}
      <div ref={rowRef} className="tb-lists-row">
        {lists.map((list, index) => (
          <SuggestionListCard
            key={list.slug}
            name={list.name}
            description={list.description}
            imageSrc={list.imageUrl ?? ""}
            href={suggestionListHref(list.slug)}
            onNavigate={() =>
              track({ event: "list_card_selected", list_id: list.id ?? list.slug, card_context: "homepage", position: index + 1 })
            }
            className="tb-lists-card"
          />
        ))}
      </div>
    </section>
  );
}
